/**
 * Passkey (WebAuthn) login card — no keys to enter; only a super-admin
 * on/off switch for the "Sign in with passkey" button on /login and /signup.
 * All existing passkeys stay registered on user accounts when disabled — they
 * simply can't be used until re-enabled.
 */
import React from "react";
import { KeyRound } from "lucide-react";
import { AuthProviderToggleRow, useAuthToggles } from "./AuthProviderToggleRow";

export const PasskeyLoginConfigCard: React.FC = () => {
  const toggles = useAuthToggles();
  const on = !!toggles.data?.passkey;
  return (
    <div className="sg-card p-6" data-testid="passkey-login-config-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-violet-50 dark:bg-violet-950/40 flex items-center justify-center shrink-0">
          <KeyRound className="h-5 w-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">Passkey Login</div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            Passwordless sign-in with Face ID / Touch ID / Windows Hello / hardware keys. No
            developer keys needed — just an on/off switch. Existing passkeys stay linked to user
            accounts when disabled; they simply can't be used until you re-enable.
          </div>
        </div>
        <span
          data-testid="passkey-login-status"
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
            on
              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900"
              : "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-400 border-slate-200 dark:border-slate-800"
          }`}
        >
          {on ? "Live" : "Disabled"}
        </span>
      </div>

      <AuthProviderToggleRow provider="passkey" label="Passkey sign-in" />
    </div>
  );
};

export default PasskeyLoginConfigCard;
